import { useEffect, useState } from "react";
import {
  Crown, Users, Globe, ChatCircleText, Sparkle, Presentation, Gauge,
  Plus, Trash, Archive, ArrowCounterClockwise, ArrowSquareOut, FloppyDisk, CurrencyDollar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const TABS = [
  ["overview", "Overview", Gauge],
  ["program", "Program", Crown],
  ["pricing", "Pricing", CurrencyDollar],
  ["content", "Content", Archive],
];

const PROPERTY_TYPES = [
  ["residential", "Residential"],
  ["commercial", "Commercial"],
  ["industrial", "Industrial"],
  ["new_construction", "New construction"],
  ["land", "Land / acreage"],
];

const FEATURES = [
  ["leadlander", "LeadLander", Globe],
  ["lpg", "Presentation Generator", Presentation],
  ["listassist", "ListAssist", Sparkle],
];

const CONTENT_KINDS = [
  ["leadlander", "LeadLander"],
  ["lpg", "Presentation Generator"],
  ["listassist", "ListAssist"],
];

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="lux-card rounded-[6px] p-4" data-testid={`pa-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-[#8A8782] text-xs uppercase tracking-wide">
        <Icon size={14} weight="duotone" className="text-[#B08D57]" />{label}
      </div>
      <p className="font-display text-3xl text-[#4A0000] mt-1">{value}</p>
    </div>
  );
}

/* ---------- Overview ---------- */
function Overview({ data, onTogglePartner }) {
  const t = data.totals || {};
  return (
    <div className="space-y-6" data-testid="pa-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Crown} label="Partners" value={`${t.partners}/${t.agents}`} />
        <Stat icon={Globe} label="LeadLanders" value={t.leadlanders} />
        <Stat icon={ChatCircleText} label="Leads" value={t.leads} />
        <Stat icon={Presentation} label="Presentations" value={t.lpg} />
      </div>

      <div className="bg-white border border-[#E9DDC6] rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F0ECE2] flex items-center gap-2">
          <Users size={16} weight="duotone" className="text-[#4A0000]" />
          <span className="font-medium text-[#4A0000]">Agents</span>
          <span className="text-xs text-[#8A8782] ml-1">— toggle Penthouse Partner access</span>
        </div>
        <div className="divide-y divide-[#F0ECE2]" data-testid="pa-agent-list">
          {data.items.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-5 py-3" data-testid={`pa-agent-${a.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#1A1715] truncate">{a.name}</p>
                <p className="text-xs text-[#8A8782] truncate">{a.company || a.email}</p>
              </div>
              <div className="hidden sm:flex items-center gap-1.5">
                <Badge className="bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6] text-[11px]">{a.leadlanders} LL</Badge>
                <Badge className="bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6] text-[11px]">{a.leads} leads</Badge>
                <Badge className="bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6] text-[11px]">{a.lpg} LPG</Badge>
              </div>
              {a.penthouse_partner && (
                <span className="text-[10px] uppercase tracking-wide text-[#8A6D1B] font-bold">{a.penthouse_source || "manual"}</span>
              )}
              <Switch
                data-testid={`pa-partner-toggle-${a.id}`}
                checked={a.penthouse_partner}
                onCheckedChange={(v) => onTogglePartner(a.id, v)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Program config ---------- */
function Program({ config, onSaved }) {
  const [cfg, setCfg] = useState(config);
  const [saving, setSaving] = useState(false);
  const setFeature = (k, v) => setCfg((c) => ({ ...c, features: { ...c.features, [k]: v } }));
  const setPerk = (i, v) => setCfg((c) => { const p = [...c.perks]; p[i] = v; return { ...c, perks: p }; });
  const addPerk = () => setCfg((c) => ({ ...c, perks: [...c.perks, ""] }));
  const removePerk = (i) => setCfg((c) => ({ ...c, perks: c.perks.filter((_, x) => x !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        monthly_price: Number(cfg.monthly_price),
        annual_price: Number(cfg.annual_price),
        features: cfg.features,
        perks: cfg.perks.map((p) => p.trim()).filter(Boolean),
      };
      const r = await client.put("/penthouse/admin/config", body);
      toast.success("Program updated");
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="pa-program">
      <div className="lux-card rounded-[6px] p-5">
        <p className="font-medium text-[#4A0000] mb-3">Pricing</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#5C5A58]">Monthly ($)</label>
            <Input type="number" data-testid="pa-monthly-price" className="mt-1 border-[#E9DDC6]"
              value={cfg.monthly_price} onChange={(e) => setCfg((c) => ({ ...c, monthly_price: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-[#5C5A58]">Annual ($)</label>
            <Input type="number" data-testid="pa-annual-price" className="mt-1 border-[#E9DDC6]"
              value={cfg.annual_price} onChange={(e) => setCfg((c) => ({ ...c, annual_price: e.target.value }))} />
          </div>
        </div>
      </div>

      <div className="lux-card rounded-[6px] p-5">
        <p className="font-medium text-[#4A0000] mb-1">Included tools</p>
        <p className="text-xs text-[#8A8782] mb-3">Turn a tool off to hide it from all agents (admins keep access).</p>
        <div className="space-y-2.5">
          {FEATURES.map(([k, label, Icon]) => (
            <div key={k} className="flex items-center gap-3 py-1" data-testid={`pa-feature-row-${k}`}>
              <Icon size={18} weight="duotone" className="text-[#B08D57]" />
              <span className="flex-1 text-sm text-[#1A1715]">{label}</span>
              <Switch data-testid={`pa-feature-${k}`} checked={!!cfg.features?.[k]} onCheckedChange={(v) => setFeature(k, v)} />
            </div>
          ))}
        </div>
      </div>

      <div className="lux-card rounded-[6px] p-5">
        <p className="font-medium text-[#4A0000] mb-1">What's included (perks)</p>
        <p className="text-xs text-[#8A8782] mb-3">Reference list of what the Penthouse Partner program includes.</p>
        <div className="space-y-2" data-testid="pa-perks">
          {cfg.perks.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input className="border-[#E9DDC6]" data-testid={`pa-perk-${i}`} value={p} onChange={(e) => setPerk(i, e.target.value)} />
              <button onClick={() => removePerk(i)} data-testid={`pa-perk-remove-${i}`} className="text-[#B4472E]/70 hover:text-[#B4472E] p-2"><Trash size={16} /></button>
            </div>
          ))}
          <button onClick={addPerk} data-testid="pa-perk-add" className="flex items-center gap-1.5 text-sm text-[#8A6D1B] hover:text-[#4A0000] transition-colors mt-1">
            <Plus size={15} weight="bold" /> Add perk
          </button>
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="pa-save-config" className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">
        <FloppyDisk size={16} className="mr-2" weight="fill" />{saving ? "Saving…" : "Save program"}
      </Button>
    </div>
  );
}

/* ---------- Content (archive / unarchive) ---------- */
function Content({ onOpenLeadLander }) {
  const [kind, setKind] = useState("leadlander");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = (k) => {
    setLoading(true);
    client.get(`/penthouse/admin/content/${k}`).then((r) => setItems(r.data.items)).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(kind); }, [kind]);

  const toggleArchive = async (id, archived) => {
    try {
      await client.post(`/penthouse/admin/content/${kind}/${id}/archive`, { archived });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, archived } : x)));
      toast.success(archived ? "Archived" : "Restored");
    } catch (e) { toast.error("Couldn't update"); }
  };

  return (
    <div className="space-y-4" data-testid="pa-content">
      <div className="flex gap-1.5">
        {CONTENT_KINDS.map(([v, label]) => (
          <button key={v} data-testid={`pa-content-tab-${v}`} onClick={() => setKind(v)}
            className={`px-3 py-1.5 rounded-md text-sm transition ${kind === v ? "bg-[#4A0000] text-white" : "bg-[#FAF9F6] text-[#5C5A58] hover:bg-[#EEE8DA]"}`}>{label}</button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-[#5C5A58]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#5C5A58] py-8 text-center border border-dashed border-[#E9DDC6] rounded-md">Nothing here yet.</p>
      ) : (
        <div className="bg-white border border-[#E9DDC6] rounded-lg divide-y divide-[#F0ECE2]" data-testid="pa-content-list">
          {items.map((it) => (
            <div key={it.id} className={`flex items-center gap-3 px-5 py-3 ${it.archived ? "bg-[#F7F1E6]/60" : ""}`} data-testid={`pa-content-item-${it.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#1A1715] truncate flex items-center gap-2">
                  {it.title}
                  {it.archived && <Badge className="bg-[#B4472E]/10 text-[#B4472E] border-[#B4472E]/25 text-[10px]">Archived</Badge>}
                </div>
                <p className="text-xs text-[#8A8782] truncate">{it.owner || "—"}{it.address ? ` · ${it.address}` : ""}</p>
              </div>
              {kind === "leadlander" && !it.archived && onOpenLeadLander && (
                <button onClick={() => onOpenLeadLander(it.id)} data-testid={`pa-open-${it.id}`}
                  className="flex items-center gap-1 text-xs text-[#5C5A58] hover:text-[#4A0000] transition-colors">
                  <ArrowSquareOut size={14} />Open
                </button>
              )}
              <button onClick={() => toggleArchive(it.id, !it.archived)} data-testid={`pa-archive-${it.id}`}
                className={`flex items-center gap-1 text-xs transition-colors ${it.archived ? "text-[#8A6D1B] hover:text-[#4A0000]" : "text-[#B4472E]/70 hover:text-[#B4472E]"}`}>
                {it.archived ? <><ArrowCounterClockwise size={14} />Restore</> : <><Archive size={14} />Archive</>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Pricing (admin-only line-item / package editor) ---------- */
function Pricing() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    client.get("/booking/admin/pricing").then((r) => setCfg(r.data)).catch(() => toast.error("Couldn't load pricing"));
  }, []);

  const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
  const setPkg = (key, field, v) => setCfg((c) => ({ ...c, packages: { ...c.packages, [key]: { ...c.packages[key], [field]: v } } }));
  const incObj = (v) => (typeof v === "string" ? { title: v, desc: "" } : { title: v?.title || v?.label || "", desc: v?.desc || "" });
  const setInclusionField = (i, field, v) => setCfg((c) => { const inc = (c.packages.all_inclusive.inclusions || []).map(incObj); inc[i] = { ...inc[i], [field]: v }; return { ...c, packages: { ...c.packages, all_inclusive: { ...c.packages.all_inclusive, inclusions: inc } } }; });
  const addInclusion = () => setCfg((c) => ({ ...c, packages: { ...c.packages, all_inclusive: { ...c.packages.all_inclusive, inclusions: [...(c.packages.all_inclusive.inclusions || []).map(incObj), { title: "", desc: "" }] } } }));
  const removeInclusion = (i) => setCfg((c) => ({ ...c, packages: { ...c.packages, all_inclusive: { ...c.packages.all_inclusive, inclusions: (c.packages.all_inclusive.inclusions || []).map(incObj).filter((_, j) => j !== i) } } }));
  const [uploadingHero, setUploadingHero] = useState(false);
  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await client.post("/booking/admin/pricing/hero-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPkg("all_inclusive", "hero_image", r.data.url);
      toast.success("Hero image updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingHero(false);
    }
  };
  const setOver = (field, v) => setCfg((c) => ({ ...c, oversized: { ...c.oversized, [field]: v } }));
  const setPh = (field, v) => setCfg((c) => ({ ...c, penthouse: { ...c.penthouse, [field]: v } }));
  const setSvc = (i, field, v) => setCfg((c) => ({ ...c, services: c.services.map((s, idx) => (idx === i ? { ...s, [field]: v } : s)) }));
  const toggleType = (i, t) => setCfg((c) => ({
    ...c,
    services: c.services.map((s, idx) => {
      if (idx !== i) return s;
      let types = (s.types || []).includes("all") ? PROPERTY_TYPES.map(([k]) => k) : [...(s.types || [])];
      types = types.includes(t) ? types.filter((x) => x !== t) : [...types, t];
      return { ...s, types };
    }),
  }));
  const hasType = (s, t) => (s.types || []).includes("all") || (s.types || []).includes(t);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        packages: {
          all_inclusive: { ...cfg.packages.all_inclusive, price: num(cfg.packages.all_inclusive.price), no_entry_price: num(cfg.packages.all_inclusive.no_entry_price), label: (cfg.packages.all_inclusive.label || "All-Inclusive").trim(), inclusions: (cfg.packages.all_inclusive.inclusions || []).map(incObj).map((x) => ({ title: (x.title || "").trim(), desc: (x.desc || "").trim() })).filter((x) => x.title) },
          photo_only: { ...cfg.packages.photo_only, price: num(cfg.packages.photo_only.price) },
        },
        oversized: { ...cfg.oversized, price: num(cfg.oversized.price), auto_sqft: num(cfg.oversized.auto_sqft), auto_acre: num(cfg.oversized.auto_acre) },
        penthouse: { monthly: num(cfg.penthouse.monthly), annual: num(cfg.penthouse.annual) },
        services: cfg.services.map((s, i) => ({ ...s, price: num(s.price), order: s.order ?? i + 1, active: s.active !== false, types: s.types || [] })),
      };
      const r = await client.put("/booking/admin/pricing", body);
      setCfg(r.data);
      toast.success("Pricing saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save pricing"); }
    setSaving(false);
  };

  if (!cfg) return <p className="text-sm text-[#5C5A58]">Loading pricing…</p>;
  const flabel = "text-[11px] uppercase tracking-wide text-[#8A8782] font-semibold";
  const dollarInput = "bg-white border-[#E9DDC6] font-mono-num";

  return (
    <div className="space-y-6" data-testid="pricing-admin">
      <p className="text-sm text-[#5C5A58]">Set every booking price, which property types each line item is offered for, and the flat oversized fee. These drive the public booking widget instantly.</p>

      {/* Packages */}
      <div className="rounded-xl border border-[#E9DDC6] bg-white p-5" data-testid="pricing-packages">
        <h3 className="font-display text-xl text-[#4A0000] mb-3">All-Inclusive package</h3>

        {/* Hero image shown on the public booking page */}
        <div className="mb-5" data-testid="hero-image-editor">
          <label className={flabel}>Hero image (shown on the booking page)</label>
          <div className="mt-1.5 flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative w-full sm:w-56 h-32 rounded-lg overflow-hidden border border-[#E9DDC6] bg-[#FAF9F6] shrink-0">
              {cfg.packages.all_inclusive.hero_image ? (
                <img src={cfg.packages.all_inclusive.hero_image} alt="All-Inclusive hero" className="w-full h-full object-cover" data-testid="hero-image-preview" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-[#8A8782]">No image — a default is shown</div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5 cursor-pointer rounded-md border border-[#B08D57]/50 text-[#8A6D1B] hover:bg-[#B08D57]/10 px-3 py-2 text-sm font-medium transition-colors" data-testid="hero-upload-btn">
                <Plus size={14} />{uploadingHero ? "Uploading…" : "Upload image"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadHero} disabled={uploadingHero} data-testid="hero-file-input" />
              </label>
              {cfg.packages.all_inclusive.hero_image && (
                <Button type="button" variant="outline" size="sm" data-testid="hero-remove-btn"
                  onClick={() => setPkg("all_inclusive", "hero_image", "")} className="border-[#E9DDC6] text-[#B4472E] hover:bg-[#B4472E]/5"><Trash size={14} className="mr-1" />Remove</Button>
              )}
              <p className="w-full text-xs text-[#8A8782]">PNG/JPEG/WEBP · up to 8 MB. Click Save to apply.</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-3">
            <label className={flabel}>Package name (shown on the booking page)</label>
            <Input data-testid="pkg-ai-label" className="mt-1 border-[#E9DDC6]" value={cfg.packages.all_inclusive.label || ""} onChange={(e) => setPkg("all_inclusive", "label", e.target.value)} />
          </div>
          <div><label className={flabel}>Price ($)</label><Input data-testid="price-ai-entry" type="number" className={`mt-1 ${dollarInput}`} value={cfg.packages.all_inclusive.price} onChange={(e) => setPkg("all_inclusive", "price", e.target.value)} /></div>
          <div><label className={flabel}>No-entry price ($)</label><Input data-testid="price-ai-noentry" type="number" className={`mt-1 ${dollarInput}`} value={cfg.packages.all_inclusive.no_entry_price} onChange={(e) => setPkg("all_inclusive", "no_entry_price", e.target.value)} /></div>
          <div><label className={flabel}>Photo Only (à la carte ref)</label><Input data-testid="price-photo" type="number" className={`mt-1 ${dollarInput}`} value={cfg.packages.photo_only.price} onChange={(e) => setPkg("photo_only", "price", e.target.value)} /></div>
        </div>

        {/* Editable inclusions shown on the booking page (title + description) */}
        <div className="mt-5">
          <label className={flabel}>What's included (title + description shown on the booking page)</label>
          <div className="space-y-2.5 mt-1.5" data-testid="inclusions-editor">
            {(cfg.packages.all_inclusive.inclusions || []).map((raw, i) => {
              const inc = incObj(raw);
              return (
              <div key={i} className="flex items-start gap-2 rounded-lg border border-[#E9DDC6] bg-white p-2">
                <div className="flex-1 space-y-1.5">
                  <Input data-testid={`inclusion-${i}`} className="border-[#E9DDC6]" value={inc.title} placeholder="Title — e.g. 360° virtual tour"
                    onChange={(e) => setInclusionField(i, "title", e.target.value)} />
                  <Input data-testid={`inclusion-desc-${i}`} className="border-[#E9DDC6] text-[13px]" value={inc.desc} placeholder="Description — e.g. Interactive walkthrough of the full space"
                    onChange={(e) => setInclusionField(i, "desc", e.target.value)} />
                </div>
                <Button type="button" variant="outline" size="icon" data-testid={`inclusion-remove-${i}`}
                  onClick={() => removeInclusion(i)} className="shrink-0 border-[#E9DDC6] text-[#B4472E] hover:bg-[#B4472E]/5"><Trash size={15} /></Button>
              </div>
              );
            })}
            {(cfg.packages.all_inclusive.inclusions || []).length === 0 && (
              <p className="text-xs text-[#8A8782]">No inclusions yet — add the deliverables buyers get.</p>
            )}
          </div>
          <Button type="button" variant="outline" size="sm" data-testid="inclusion-add" onClick={addInclusion}
            className="mt-2 border-[#B08D57]/50 text-[#8A6D1B] hover:bg-[#B08D57]/10"><Plus size={14} className="mr-1" />Add inclusion</Button>
        </div>
      </div>

      {/* Oversized */}
      <div className="rounded-xl border border-[#E9DDC6] bg-white p-5" data-testid="pricing-oversized">
        <h3 className="font-display text-xl text-[#4A0000] mb-1">Oversized fee</h3>
        <p className="text-xs text-[#5C5A58] mb-3">One flat fee for oversized properties (unlimited sq ft / acreage). Auto-triggers above the thresholds; agents & staff can override per booking.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><label className={flabel}>Flat fee ($)</label><Input data-testid="price-oversized" type="number" className={`mt-1 ${dollarInput}`} value={cfg.oversized.price} onChange={(e) => setOver("price", e.target.value)} /></div>
          <div><label className={flabel}>Auto above (sq ft)</label><Input data-testid="price-oversized-sqft" type="number" className={`mt-1 ${dollarInput}`} value={cfg.oversized.auto_sqft} onChange={(e) => setOver("auto_sqft", e.target.value)} /></div>
          <div><label className={flabel}>Auto above (acres)</label><Input data-testid="price-oversized-acre" type="number" className={`mt-1 ${dollarInput}`} value={cfg.oversized.auto_acre} onChange={(e) => setOver("auto_acre", e.target.value)} /></div>
        </div>
        <div className="mt-3"><label className={flabel}>Label</label><Input className={`mt-1 bg-white border-[#E9DDC6]`} value={cfg.oversized.label || ""} onChange={(e) => setOver("label", e.target.value)} /></div>
      </div>

      {/* Penthouse subscription */}
      <div className="rounded-xl border border-[#E9DDC6] bg-white p-5" data-testid="pricing-penthouse">
        <h3 className="font-display text-xl text-[#4A0000] mb-3">Penthouse Partner</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={flabel}>Monthly ($)</label><Input type="number" className={`mt-1 ${dollarInput}`} value={cfg.penthouse.monthly} onChange={(e) => setPh("monthly", e.target.value)} /></div>
          <div><label className={flabel}>Annual ($)</label><Input type="number" className={`mt-1 ${dollarInput}`} value={cfg.penthouse.annual} onChange={(e) => setPh("annual", e.target.value)} /></div>
        </div>
      </div>

      {/* Services / line items */}
      <div className="rounded-xl border border-[#E9DDC6] bg-white p-5" data-testid="pricing-services">
        <h3 className="font-display text-xl text-[#4A0000] mb-1">À la carte line items</h3>
        <p className="text-xs text-[#5C5A58] mb-4">Toggle a line item off to hide it everywhere. Check the property types it's offered for — land/acreage should only have drone.</p>
        <div className="space-y-3">
          {cfg.services.map((s, i) => (
            <div key={s.key} className={`rounded-lg border p-4 transition ${s.active !== false ? "border-[#E9DDC6] bg-[#FAF9F6]" : "border-[#E9DDC6] bg-[#F4F2EE] opacity-70"}`} data-testid={`svc-row-${s.key}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <Switch data-testid={`svc-active-${s.key}`} checked={s.active !== false} onCheckedChange={(v) => setSvc(i, "active", v)} />
                <Input className="bg-white border-[#E9DDC6] w-40" value={s.label} onChange={(e) => setSvc(i, "label", e.target.value)} data-testid={`svc-label-${s.key}`} />
                <div className="flex items-center gap-1"><span className="text-[#8A8782] font-mono">$</span><Input type="number" className={`${dollarInput} w-28`} value={s.price} onChange={(e) => setSvc(i, "price", e.target.value)} data-testid={`svc-price-${s.key}`} /></div>
                <Badge className="bg-[#4A0000]/5 text-[#4A0000] border-[#4A0000]/10 font-mono text-[10px]">{s.key}</Badge>
              </div>
              <Input className="bg-white border-[#E9DDC6] mt-2 text-sm" value={s.desc || ""} placeholder="Short description shown at booking" onChange={(e) => setSvc(i, "desc", e.target.value)} data-testid={`svc-desc-${s.key}`} />
              <div className="flex flex-wrap gap-2 mt-2">
                {PROPERTY_TYPES.map(([t, label]) => (
                  <button key={t} type="button" onClick={() => toggleType(i, t)} data-testid={`svc-type-${s.key}-${t}`}
                    className={`text-xs rounded-full px-2.5 py-1 border transition ${hasType(s, t) ? "border-[#B08D57] bg-[#B08D57]/12 text-[#8A6D1B] font-medium" : "border-[#E9DDC6] bg-white text-[#8A8782] hover:border-[#D9CFB0]"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end sticky bottom-4">
        <Button data-testid="pricing-save" onClick={save} disabled={saving} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white shadow-lg">
          <FloppyDisk size={16} weight="fill" className="mr-1.5" />{saving ? "Saving…" : "Save pricing"}
        </Button>
      </div>
    </div>
  );
}


export default function PenthouseAdmin({ onOpenLeadLander }) {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);

  const load = () => client.get("/penthouse/admin/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const togglePartner = async (id, enabled) => {
    try {
      await client.post(`/penthouse/admin/partner/${id}`, { enabled });
      setData((d) => ({ ...d, items: d.items.map((a) => (a.id === id ? { ...a, penthouse_partner: enabled, penthouse_source: enabled ? "manual" : null } : a)), totals: { ...d.totals, partners: d.totals.partners + (enabled ? 1 : -1) } }));
      toast.success(enabled ? "Penthouse Partner enabled" : "Access removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="penthouse-admin">
      <div>
        <p className="eyebrow text-xs text-[#B08D57] mb-1">Management</p>
        <h2 className="font-display text-3xl text-[#4A0000]">Penthouse Partner</h2>
        <p className="text-sm text-[#5C5A58] mt-1">Oversee the program, grant or revoke partner access, and manage every agent's LeadLander pages, presentations and copy.</p>
      </div>

      <div className="flex gap-1.5 border-b border-[#E9DDC6] pb-3">
        {TABS.map(([v, label, Icon]) => (
          <button key={v} data-testid={`pa-tab-${v}`} onClick={() => setTab(v)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm transition ${tab === v ? "bg-[#4A0000] text-white" : "text-[#5C5A58] hover:bg-[#FAF9F6]"}`}>
            <Icon size={16} weight={tab === v ? "fill" : "duotone"} />{label}
          </button>
        ))}
      </div>

      {!data ? (
        <p className="text-sm text-[#5C5A58]">Loading…</p>
      ) : (
        <>
          {tab === "overview" && <Overview data={data} onTogglePartner={togglePartner} />}
          {tab === "program" && <Program config={data.config} onSaved={(cfg) => setData((d) => ({ ...d, config: cfg }))} />}
          {tab === "pricing" && <Pricing />}
          {tab === "content" && <Content onOpenLeadLander={onOpenLeadLander} />}
        </>
      )}
    </div>
  );
}
